import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SignaturePad from './SignaturePad';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// ── PDF.js loader ─────────────────────────────────────────────────────────────

function loadPdfJs() {
  return new Promise((resolve) => {
    if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    s.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      resolve(window.pdfjsLib);
    };
    document.head.appendChild(s);
  });
}

// ── Field config ──────────────────────────────────────────────────────────────

const FIELD_CFG = {
  signature: { color: '#A62B1F', bg: '#F8ECE9', label: 'Sign here' },
  text:      { color: '#3B82F6', bg: '#EFF6FF', label: 'Enter text' },
  date:      { color: '#10B981', bg: '#ECFDF5', label: 'Date' },
};
const PREFILL_CFG = { color: '#F59E0B', bg: '#FFFBEB' };

// ── Main SignPage ─────────────────────────────────────────────────────────────

export default function SignPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [doc, setDoc] = useState(null);            // data from /view/:token
  const [pages, setPages] = useState([]);          // rendered PDF pages
  const [pdfLoading, setPdfLoading] = useState(false);
  const [fieldValues, setFieldValues] = useState({}); // { fieldId: value }
  const [activeSignField, setActiveSignField] = useState(null); // fieldId awaiting sig pad
  const [consented, setConsented] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);
  const [auditInfo, setAuditInfo] = useState(null); // { signedAt, refId }

  const isReviewer = doc?.role === 'reviewer';
  const alreadyDone = doc?.party_status && doc.party_status !== 'pending';

  // ── Load doc ────────────────────────────────────────────────────────────────

  useEffect(() => {
    axios.get(`${API}/api/esign/view/${token}`)
      .then(r => {
        setDoc(r.data);
        // Date fields are the date you sign: start them at today (you can still change one)
        const d = new Date();
        const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const dates = Object.fromEntries((r.data.fields || []).filter(f => f.type === 'date' && f.role !== 'prefill').map(f => [f.id, today]));
        setFieldValues(prev => ({ ...dates, ...prev }));
        setLoading(false);
      })
      .catch(err => {
        setError(err?.response?.data?.detail || 'This signing link is invalid or has already been used.');
        setLoading(false);
      });
  }, [token]);

  // ── Render PDF ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!doc) return;
    let cancelled = false;
    setPdfLoading(true);
    (async () => {
      try {
        const pdfjsLib = await loadPdfJs();
        const bytes = Uint8Array.from(atob(doc.content_b64), c => c.charCodeAt(0));
        const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
        const rendered = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.6 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          rendered.push({ canvas, pageNum: i });
        }
        if (!cancelled) { setPages(rendered); setPdfLoading(false); }
      } catch {
        if (!cancelled) setPdfLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [doc]);

  // ── Field interaction ────────────────────────────────────────────────────────

  const handleFieldClick = (field) => {
    if (field.type === 'signature') {
      setActiveSignField(field.id);
    }
    // text/date fields are handled inline (contenteditable or input)
  };

  const applySignature = (dataUrl) => {
    setFieldValues(prev => ({ ...prev, [activeSignField]: dataUrl }));
    setActiveSignField(null);
  };

  const updateTextValue = (fieldId, value) => {
    setFieldValues(prev => ({ ...prev, [fieldId]: value }));
  };

  // ── Fields ────────────────────────────────────────────────────────────────────
  // /view/:token already scopes `fields` to just this party's own fields, plus
  // any pre-resolved 'prefill' fields (read-only, filled in by the sender).
  const myFields = (doc?.fields || []).filter(f => f.role !== 'prefill');
  const prefillFields = (doc?.fields || []).filter(f => f.role === 'prefill');
  const requiredFields = myFields.filter(f => f.type === 'signature');
  const allFilled = isReviewer
    ? true
    : requiredFields.length ? requiredFields.every(f => fieldValues[f.id]) : true;
  const unfilledCount = requiredFields.filter(f => !fieldValues[f.id]).length;
  const hasFields = requiredFields.length > 0;

  // For the no-fields fallback, collect a global sig from a dedicated canvas
  const globalSigRef = useRef(null);
  const [globalHasSig, setGlobalHasSig] = useState(false);
  const globalIsDrawing = useRef(false);

  // If there are no fields and this is a signer, show the old-style canvas
  useEffect(() => {
    if (!doc || isReviewer || myFields.length || !globalSigRef.current) return;
    const canvas = globalSigRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1E2A33'; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    const pos = (e) => { const r = canvas.getBoundingClientRect(); const s = e.touches ? e.touches[0] : e; return { x: (s.clientX - r.left) * (canvas.width / r.width), y: (s.clientY - r.top) * (canvas.height / r.height) }; };
    const start = (e) => { e.preventDefault(); globalIsDrawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { e.preventDefault(); if (!globalIsDrawing.current) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setGlobalHasSig(true); };
    const end = () => { globalIsDrawing.current = false; };
    canvas.addEventListener('mousedown', start); canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end); canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);
    return () => {
      canvas.removeEventListener('mousedown', start); canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', end); canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start); canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, [doc, isReviewer, myFields.length]);

  // ── Submit ───────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!consented) { alert('Please confirm the electronic signature disclosure below.'); return; }
    setSubmitting(true);
    try {
      let payload = { consented: true };
      const fieldValuesArr = Object.entries(fieldValues).map(([id, value]) => ({ id, value }));
      const sigFieldId = myFields.find(f => f.type === 'signature')?.id;

      if (!isReviewer) {
        const signatureDataUrl = myFields.length
          ? (fieldValues[sigFieldId] || 'fields-submitted')
          : globalSigRef.current?.toDataURL('image/png');

        if (!signatureDataUrl) { alert('Please draw your signature'); setSubmitting(false); return; }

        payload = { ...payload, signature_data: signatureDataUrl, field_values: fieldValuesArr };
      } else if (fieldValuesArr.length) {
        // Optional — a reviewer may choose to sign/initial a field, but it's never required
        payload = { ...payload, field_values: fieldValuesArr };
        if (sigFieldId && fieldValues[sigFieldId]) payload.signature_data = fieldValues[sigFieldId];
      }

      const res = await axios.post(`${API}/api/esign/sign/${token}`, payload);
      setAuditInfo({ signedAt: res.data.signed_at, refId: token.slice(0, 8).toUpperCase() });
      setSigned(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to submit. Please try again.';
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) return <Shell><p style={{ color: '#5C6670' }}>Loading document…</p></Shell>;
  if (error) return (
    <Shell>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <h2 style={{ color: '#1E2A33', marginBottom: 8 }}>Link unavailable</h2>
        <p style={{ color: '#5C6670' }}>{error}</p>
      </div>
    </Shell>
  );
  if (signed || alreadyDone) return (
    <Shell>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
        <h2 style={{ color: '#1E2A33', marginBottom: 8 }}>{isReviewer ? 'Review recorded' : 'Document signed'}</h2>
        <p style={{ color: '#5C6670' }}>
          Thank you. {isReviewer ? 'Your review has' : 'Your signature has'} been recorded for <strong>{doc.document_title}</strong>.
        </p>
        <p style={{ color: '#9AA3AB', fontSize: 13, marginTop: 8 }}>
          The document will be finalized once every party has completed their part.
        </p>
        {auditInfo && (
          <div style={{ marginTop: 20, padding: '14px 16px', background: '#FBF8F2', borderRadius: 8, border: '1px solid #E3DDD1', textAlign: 'left' }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#9AA3AB', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Audit Record</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <p style={{ fontSize: 12, color: '#5C6670' }}>📄 <strong style={{ color: '#1E2A33' }}>{doc.document_title}</strong></p>
              <p style={{ fontSize: 12, color: '#5C6670' }}>✍ {isReviewer ? 'Reviewed' : 'Signed'} by <strong style={{ color: '#1E2A33' }}>{doc.signer_name || doc.signer_email}</strong></p>
              <p style={{ fontSize: 12, color: '#5C6670' }}>🕐 {auditInfo.signedAt ? new Date(auditInfo.signedAt).toLocaleString() : new Date().toLocaleString()}</p>
              <p style={{ fontSize: 12, color: '#5C6670' }}>🔑 Reference: <strong style={{ color: '#1E2A33', fontFamily: 'monospace' }}>{auditInfo.refId}</strong></p>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );

  return (
    <Shell wide>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <p style={{ fontSize: 13, color: '#5C6670', marginBottom: 4 }}>Sent by {doc.sent_by}</p>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1E2A33', marginBottom: 4 }}>{doc.document_title}</h2>
        <p style={{ fontSize: 14, color: '#5C6670' }}>
          {isReviewer ? 'Reviewing' : 'Signing'} as <strong>{doc.signer_name || doc.signer_email}</strong>
          {isReviewer && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: '#A62B1F', background: '#F8ECE9', padding: '2px 8px', borderRadius: 999 }}>REVIEW ONLY</span>}
        </p>
      </div>

      {/* Top submit button — mirrors the one at the bottom so a signer doesn't
          have to scroll through the whole document to find it, especially once
          they've already filled everything and just need to confirm. */}
      <div style={{ marginBottom: 20 }}>
        <SubmitButton
          submitting={submitting}
          consented={consented}
          canSubmit={consented && (isReviewer || (hasFields ? allFilled : globalHasSig))}
          isReviewer={isReviewer}
          onClick={handleSubmit}
        />
      </div>

      {/* PDF with field overlays */}
      <div style={{ marginBottom: 24 }}>
        {pdfLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#5C6670' }}>Rendering document…</div>
        ) : pages.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {pages.map((pg) => {
              const pageMyFields = myFields.filter(f => f.page === pg.pageNum);
              const pagePrefillFields = prefillFields.filter(f => f.page === pg.pageNum);
              return (
                <div key={pg.pageNum} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
                  <canvas
                    ref={el => { if (el) { el.width = pg.canvas.width; el.height = pg.canvas.height; el.getContext('2d').drawImage(pg.canvas, 0, 0); } }}
                    style={{ display: 'block', width: '100%', height: 'auto' }}
                  />
                  {/* Read-only prefilled values */}
                  {pagePrefillFields.map(f => (
                    <div
                      key={f.id}
                      style={{
                        position: 'absolute',
                        left: `${f.x_pct}%`, top: `${f.y_pct}%`,
                        width: `${f.w_pct || 28}%`, height: `${f.h_pct || 5}%`,
                        border: `1.5px dashed ${PREFILL_CFG.color}`, background: PREFILL_CFG.bg,
                        borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, color: '#1E2A33', overflow: 'hidden', padding: '0 4px',
                      }}
                    >
                      {f.value}
                    </div>
                  ))}
                  {/* Fields for me — reviewers can optionally fill these too, just never required */}
                  {pageMyFields.map(f => {
                    const cfg = FIELD_CFG[f.type];
                    const filled = !!fieldValues[f.id];
                    const isSig = f.type === 'signature';
                    return (
                      <div
                        key={f.id}
                        onClick={() => handleFieldClick(f)}
                        style={{
                          position: 'absolute',
                          left: `${f.x_pct}%`,
                          top: `${f.y_pct}%`,
                          width: `${f.w_pct || (f.type === 'signature' ? 28 : f.type === 'date' ? 18 : 28)}%`,
                          height: `${f.h_pct || (f.type === 'signature' ? 8 : 5)}%`,
                          borderBottom: `2px solid ${filled ? '#9AA3AB' : cfg.color}`,
                          background: 'transparent',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: isSig && !filled ? 'flex-start' : 'center',
                          cursor: isSig ? 'pointer' : 'default',
                          overflow: 'visible',
                        }}
                      >
                        {isSig && filled ? (
                          <img src={fieldValues[f.id]} alt="signature" style={{ maxWidth: '100%', maxHeight: '160%', objectFit: 'contain', paddingBottom: 2 }} />
                        ) : isSig ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            fontSize: 11, fontWeight: 700, color: '#fff',
                            background: cfg.color, padding: '3px 9px 3px 7px',
                            borderRadius: '3px 3px 3px 0', transform: 'translateY(2px)',
                            whiteSpace: 'nowrap', boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                          }}>✎ {f.label || 'Sign'}</span>
                        ) : (
                          <input
                            type={f.type === 'date' ? 'date' : 'text'}
                            placeholder={f.label || cfg.label}
                            value={fieldValues[f.id] || ''}
                            onChange={e => updateTextValue(f.id, e.target.value)}
                            onClick={e => e.stopPropagation()}
                            style={{
                              width: '100%', height: '100%', border: 'none', background: 'transparent',
                              textAlign: 'center', fontSize: 12, color: '#1E2A33', outline: 'none', padding: '0 4px',
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ) : (
          // Fallback: iframe if PDF.js fails
          <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #E3DDD1' }}>
            <iframe
              title="Document"
              src={`data:${doc.content_type};base64,${doc.content_b64}`}
              style={{ width: '100%', height: 500, border: 'none', display: 'block' }}
            />
          </div>
        )}
      </div>

      {/* Global signature canvas (only when no fields were placed, signer only) */}
      {!isReviewer && !hasFields && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ fontSize: 14, fontWeight: 600, color: '#1E2A33' }}>Draw your signature</label>
            <button
              onClick={() => { globalSigRef.current?.getContext('2d').clearRect(0, 0, 560, 140); setGlobalHasSig(false); }}
              style={{ fontSize: 13, color: '#5C6670', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
            >Clear</button>
          </div>
          <canvas
            ref={globalSigRef}
            width={560} height={140}
            style={{ width: '100%', height: 140, border: '1px dashed #D3CCBE', borderRadius: 8, background: '#FBF8F2', cursor: 'crosshair', touchAction: 'none', display: 'block' }}
          />
          {!globalHasSig && <p style={{ fontSize: 12, color: '#9AA3AB', marginTop: 6 }}>Sign above using your mouse or finger</p>}
        </div>
      )}

      {/* Consent disclosure */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 16, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={consented}
          onChange={e => setConsented(e.target.checked)}
          style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }}
        />
        <span style={{ fontSize: 13, color: '#5C6670', lineHeight: 1.5 }}>
          {doc.consent_text || 'I agree to sign this document electronically and confirm my electronic signature is legally binding, same as a handwritten signature.'}
        </span>
      </label>

      {/* Submit */}
      <SubmitButton
        submitting={submitting}
        consented={consented}
        canSubmit={consented && (isReviewer || (hasFields ? allFilled : globalHasSig))}
        isReviewer={isReviewer}
        onClick={handleSubmit}
      />

      {!isReviewer && !allFilled && unfilledCount > 0 && (
        <p style={{ fontSize: 12, color: '#A62B1F', marginTop: 8, textAlign: 'center' }}>
          Please draw your signature ({unfilledCount} signature field{unfilledCount > 1 ? 's' : ''} remaining)
        </p>
      )}

      {/* Signature pad modal */}
      {activeSignField && (
        <SignaturePad onDone={applySignature} onCancel={() => setActiveSignField(null)} />
      )}
    </Shell>
  );
}

function SubmitButton({ submitting, consented, canSubmit, isReviewer, onClick }) {
  const enabled = !submitting && canSubmit;
  return (
    <button
      onClick={onClick}
      disabled={submitting || !canSubmit}
      style={{
        width: '100%', padding: '13px 0',
        background: enabled ? '#A62B1F' : '#E3DDD1',
        color: enabled ? '#fff' : '#9AA3AB',
        border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 15,
        cursor: enabled ? 'pointer' : 'not-allowed',
      }}
    >
      {submitting ? 'Submitting…' : isReviewer ? 'Confirm Review' : 'Sign Document'}
    </button>
  );
}

function Shell({ children, wide }) {
  return (
    <div style={{
      minHeight: '100vh', background: '#F3EFE7',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '40px 16px',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    }}>
      <div style={{
        width: '100%', maxWidth: wide ? 780 : 620,
        background: '#fff', borderRadius: 12, padding: '36px 32px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      }}>
        <div style={{ marginBottom: 28 }}>
          <img src="/stagehead-os-lockup-light.svg" alt="Stagehead OS" style={{ height: 36, display: 'block' }} />
        </div>
        {children}
      </div>
    </div>
  );
}
